import Seo from '@/components/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { MapPin, Phone, Mail } from 'lucide-react';

const Datenschutz = () => {
  return (
    <div className="min-h-screen bg-background">
      <Seo title="Datenschutz | SR-Zerspanung" description="Datenschutzerklärung der SR-Zerspanung, Stamsried." path="/datenschutz" noindex />
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-8">
            Datenschutzerklärung
          </h1>
          
          <div className="prose prose-lg max-w-none text-muted-foreground">
            {/* Einleitung */}
            <section className="mb-8">
              <p>
                Der Schutz und die Sicherheit von persönlichen Daten hat bei uns eine hohe Priorität. Daher halten wir uns strikt an die Regeln des deutschen Bundesdatenschutzgesetzes (BDSG). Nachfolgend werden Sie darüber informiert, welche Art von Daten erfasst und zu welchem Zweck sie erhoben werden:
              </p>
            </section>

            {/* Verantwortliche Stelle */}
            <section className="mb-8">
              <p className="text-foreground font-medium mb-4">
                Verantwortliche Stelle im Sinne der Datenschutzgesetze, insbesondere der EU-Datenschutzgrundverordnung (DSGVO), ist:
              </p>
              
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-foreground font-bold mb-2">Thomas Schindler</p>
                <h3 className="font-heading font-bold text-xl text-foreground mb-4">S&R Zerspanung</h3>
                <p className="text-foreground mb-4">Inhaber: Thomas Schindler</p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Bernbachstr. 9<br />93491 Stamsried</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                    <a href="tel:+4994669115130" className="hover:text-primary transition-colors">
                      09466 / 911 51 30
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                    <a href="mailto:thomas.schindler@sr-zerspanung.de" className="hover:text-primary transition-colors">
                      thomas.schindler@sr-zerspanung.de
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Betroffenenrechte */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Ihre Betroffenenrechte</h2>
              <p>Unter den angegebenen Kontaktdaten können Sie jederzeit folgende Rechte ausüben:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Auskunft über Ihre bei uns gespeicherten Daten und deren Verarbeitung,</li>
                <li>Berichtigung unrichtiger personenbezogener Daten,</li>
                <li>Löschung Ihrer bei uns gespeicherten Daten,</li>
                <li>Einschränkung der Datenverarbeitung, sofern wir Ihre Daten aufgrund gesetzlicher Pflichten noch nicht löschen dürfen,</li>
                <li>Widerspruch gegen die Verarbeitung Ihrer Daten bei uns und</li>
                <li>Datenübertragbarkeit, sofern Sie in die Datenverarbeitung eingewilligt haben oder einen Vertrag mit uns abgeschlossen haben.</li>
              </ul>
              <p className="mt-4">
                Sofern Sie uns eine Einwilligung erteilt haben, können Sie diese jederzeit mit Wirkung für die Zukunft widerrufen.
              </p>
              <p className="mt-4">
                Sie können sich jederzeit mit einer Beschwerde an die für Sie zuständige Aufsichtsbehörde wenden. Ihre zuständige Aufsichtsbehörde richtet sich nach dem Bundesland Ihres Wohnsitzes, Ihrer Arbeit oder der mutmaßlichen Verletzung. Eine Liste der Aufsichtsbehörden (für den nichtöffentlichen Bereich) mit Anschrift finden Sie unter:{' '}
                <a href="https://www.bfdi.bund.de/DE/Infothek/Anschriften_Links/anschriften_links-node.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  www.bfdi.bund.de
                </a>.
              </p>
            </section>

            {/* Datenübermittlung */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Datenübermittlung / Datenprotokollierung</h2>
              <p>
                Beim Besuch dieser Seite verzeichnet der Web-Server automatisch Log-Files, die keiner bestimmten Person zugeordnet werden können. Diese Daten beinhalten z. B. den Browsertyp und -version, verwendetes Betriebssystem, Referrer URL (die zuvor besuchte Seite), IP-Adresse des anfragenden Rechners, Zugriffsdatum und -uhrzeit der Serveranfrage und die Dateianfrage des Client (Dateiname und URL).
              </p>
              <p className="mt-4">
                Diese Daten werden nur zum Zweck der statistischen Auswertung gesammelt. Eine Weitergabe an Dritte, zu kommerziellen oder nichtkommerziellen Zwecken, findet nicht statt.
              </p>
            </section>

            {/* Nutzung persönlicher Daten */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Nutzung persönlicher Daten</h2>
              <p>
                Persönliche Daten werden nur erhoben oder verarbeitet, wenn Sie diese Angaben freiwillig, z.B. im Rahmen einer Anfrage mitteilen. Sofern keine erforderlichen Gründe im Zusammenhang mit einer Geschäftsabwicklung bestehen, können Sie jederzeit die zuvor erteilte Genehmigung Ihrer persönlichen Datenspeicherung mit sofortiger Wirkung schriftlich (z.B. per E-Mail oder per Fax) widerrufen.
              </p>
              <p className="mt-4">
                Ihre Daten werden nicht an Dritte weitergeben, es sei denn, eine Weitergabe ist aufgrund gesetzlicher Vorschriften erforderlich.
              </p>
            </section>

            {/* Auskunft, Änderung und Löschung */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Auskunft, Änderung und Löschung Ihrer Daten</h2>
              <p>
                Gemäß geltendem Recht können Sie jederzeit bei uns schriftlich nachfragen, ob und welche personenbezogenen Daten bei uns über Sie gespeichert sind. Eine entsprechende Mitteilung hierzu erhalten Sie umgehend.
              </p>
            </section>

            {/* Sicherheit */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Sicherheit Ihrer Daten</h2>
              <p>
                Ihre uns zur Verfügung gestellten persönlichen Daten werden durch Ergreifung aller technischen sowie organisatorischen Sicherheitsmaßnahmen so gesichert, dass sie für den Zugriff unberechtigter Dritter unzugänglich sind.
              </p>
              <p className="mt-4">
                Bei Versendung von sehr sensiblen Daten oder Informationen ist es empfehlenswert, den Postweg zu nutzen, da eine vollständige Datensicherheit per E-Mail nicht gewährleistet werden kann.
              </p>
            </section>

            {/* Google Analytics */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Hinweis zu Google Analytics</h2>
              <p>
                Diese Website benutzt Google Analytics, einen Webanalysedienst der Google Inc. („Google"). Google Analytics verwendet sog. „Cookies", d. h. Textdateien, die auf Ihrem Computer gespeichert werden und die eine Analyse der Benutzung der Website durch Sie ermöglicht.
              </p>
              <p className="mt-4">
                Die durch den Cookie erzeugten Informationen über Ihre Benutzung diese Website (einschließlich Ihrer IP-Adresse) wird an einen Server von Google in den USA übertragen und dort gespeichert. Google wird diese Informationen benutzen, um Ihre Nutzung der Website auszuwerten, um Reports über die Websiteaktivitäten für die Websitebetreiber zusammenzustellen und um weitere mit der Websitenutzung und der Internetnutzung verbundene Dienstleistungen zu erbringen.
              </p>
              <p className="mt-4">
                Auch wird Google diese Informationen gegebenenfalls an Dritte übertragen, sofern dies gesetzlich vorgeschrieben oder soweit Dritte diese Daten im Auftrag von Google verarbeiten. Google wird in keinem Fall Ihre IP-Adresse mit anderen Daten der Google in Verbindung bringen.
              </p>
              <p className="mt-4">
                Sie können die Installation der Cookies durch eine entsprechende Einstellung Ihrer Browser Software verhindern; wir weisen Sie jedoch darauf hin, dass Sie in diesem Fall gegebenenfalls nicht sämtliche Funktionen dieser Website voll umfänglich nutzen können. Durch die Nutzung dieser Website erklären Sie sich mit der Bearbeitung der über Sie erhobenen Daten durch Google in der zuvor beschriebenen Art und Weise und zu dem zuvor benannten Zweck einverstanden.
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Cookies</h2>
              <p>
                Auf einigen unserer Seiten verwenden wir sog. "Session-Cookies", um Ihnen die Nutzung unserer Webseiten zu erleichtern. Dabei handelt es sich um kleine Textdateien, die nur für die Dauer Ihres Besuchs unserer Webseite auf Ihrer Festplatte hinterlegt und abhängig von der Einstellung Ihres Browser-Programms beim Beenden des Browsers wieder gelöscht werden.
              </p>
              <p className="mt-4">
                Diese Cookies rufen keine auf Ihrer Festplatte über Sie gespeicherten Informationen ab und beeinträchtigen nicht Ihren PC oder ihre Dateien. Die meisten Browser sind so eingestellt, dass sie Cookies automatisch akzeptieren. Sie können das Speichern von Cookies jedoch deaktivieren oder ihren Browser so einstellen, dass er Sie auf die Sendung von Cookies hinweist.
              </p>
            </section>

            {/* Änderungen */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Änderungen dieser Datenschutzbestimmungen</h2>
              <p>
                Wir werden diese Richtlinien zum Schutz Ihrer persönlichen Daten von Zeit zu Zeit aktualisieren. Sie sollten sich diese Richtlinien gelegentlich ansehen, um auf dem Laufenden darüber zu bleiben, wie wir Ihre Daten schützen und die Inhalte unserer Website stetig verbessern.
              </p>
              <p className="mt-4">
                Sollten wir wesentliche Änderungen bei der Sammlung, der Nutzung und/oder der Weitergabe der uns von Ihnen zur Verfügung gestellten personenbezogenen Daten vornehmen, werden wir Sie durch einen eindeutigen und gut sichtbaren Hinweis auf der Website darauf aufmerksam machen.
              </p>
              <p className="mt-4">
                Mit der Nutzung der Webseite erklären Sie sich mit den Bedingungen dieser Richtlinien zum Schutz persönlicher Daten einverstanden.
              </p>
            </section>

            {/* Kontaktformular */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Kontaktformular</h2>
              <p>
                Treten Sie bzgl. Fragen jeglicher Art per E-Mail oder Kontaktformular mit uns in Kontakt, erteilen Sie uns zum Zwecke der Kontaktaufnahme Ihre freiwillige Einwilligung. Hierfür ist die Angabe einer validen E-Mail-Adresse erforderlich. Diese dient der Zuordnung der Anfrage und der anschließenden Beantwortung derselben.
              </p>
              <p className="mt-4">
                Die Angabe weiterer Daten ist optional. Die von Ihnen gemachten Angaben werden zum Zwecke der Bearbeitung der Anfrage sowie für mögliche Anschlussfragen gespeichert. Nach Erledigung der von Ihnen gestellten Anfrage werden personenbezogene Daten automatisch gelöscht.
              </p>
              <p className="mt-4">
                Die Übermittlung Ihrer Daten erfolgt verschlüsselt.
              </p>
            </section>

            {/* Google Maps */}
            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl text-foreground mb-4">Verwendung von Google Maps</h2>
              <p>
                Diese Webseite verwendet Google Maps API, um geographische Informationen visuell darzustellen. Bei der Nutzung von Google Maps werden von Google auch Daten über die Nutzung der Kartenfunktionen durch Besucher erhoben, verarbeitet und genutzt.
              </p>
              <p className="mt-4">
                Nähere Informationen über die Datenverarbeitung durch Google können Sie den Google-Datenschutzhinweisen entnehmen. Dort können Sie im Datenschutzcenter auch Ihre persönlichen Datenschutz-Einstellungen verändern.
              </p>
              <p className="mt-4">
                Ausführliche Anleitungen zur Verwaltung der eigenen Daten im Zusammenhang mit Google-Produkten finden Sie{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  hier
                </a>.
              </p>
            </section>

            {/* Kontakt */}
            <section className="mb-8 bg-card rounded-xl p-6 border border-border">
              <p className="text-foreground">
                Bei Fragen zu diesen Datenschutzbestimmungen wenden Sie sich bitte über unsere{' '}
                <a href="/impressum" className="text-primary hover:underline">Kontakt-Seite</a>{' '}
                an uns.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Datenschutz;